import {createAlova, invalidateCache} from 'alova'
import adapterFetch from 'alova/fetch'
import {authClient} from '~/lib/auth-client'

class MyHttpService {
  private readonly defaultCacheConfig = {
    mode: 'memory',
    expire: 3600000 // 1 hour
  } as const

  public readonly alovaInst

  constructor() {
    this.alovaInst = createAlova({
      baseURL: '',
      cacheLogger: false,
      requestAdapter: adapterFetch(),

      async beforeRequest(method) {
        method.config.headers = method.config.headers || {}
        method.config.credentials = 'include' // This is what handles session cookies

        // If you are using BetterAuth's built-in token management:
        // Most BetterAuth implementations don't require manual header injection
        // if you are on the same domain (due to cookies).

        try {
          // Use the .token() method provided by the JWT plugin
          const {data, error} = await authClient.token()

          if (data && data.token) {
            method.config.headers.Authorization = `Bearer ${data.token}`
            console.log("jwttoken::" +    method.config.headers.Authorization )
          } else if (error) {
            console.error('Failed to retrieve JWT token:', error)
          }
        } catch (err) {
          console.error('Error during token retrieval:', err)
        }

        // 2. Handle Locale Headers (Safely separated)
        try {
          // Use Nuxt's global application context instead of the useI18n composable
          const {$i18n} = useNuxtApp()
          const currentLocale = $i18n?.locale?.value || 'en-nz'

          method.config.headers['Accept-Language'] = currentLocale
          method.config.headers['X-Locale'] = currentLocale
        } catch (localeError) {
          console.error('Failed to resolve locale via useNuxtApp:', localeError)
          // Fallback if the Nuxt context isn't fully ready (e.g., during early SSR)
          method.config.headers['Accept-Language'] = 'en-nz'
          method.config.headers['X-Locale'] = 'en-nz'
        }
      },

      responded: {
        onSuccess: async (response, method) => {
          const contentType = response.headers.get('content-type')
          const isJson = contentType && contentType.includes('application/json')

          if (!response.ok) {
            let errorMessage = `Error ${response.status}: ${response.statusText}`
            if (isJson) {
              const errorData = await response.json().catch(() => ({}))
              errorMessage = errorData.message || errorMessage
            }
            throw new Error(errorMessage)
          }

          return isJson ? response.json() : response.text()
        },

        onError: (err, method) => {
          const startTime = (method.config as any).startTime
          const duration = startTime ? Date.now() - startTime : 'N/A'

          console.error(
            `%c [Request Failed] %c ${method.url} %c (${duration}ms)`,
            'color: red; font-weight: bold',
            'color: inherit',
            'color: orange'
          )
          throw err
        }
      }
    })
  }

  async get<T>(url: string, force = false): Promise<T> {
    return this.alovaInst.Get<T>(url, {cacheFor: this.defaultCacheConfig}).send(force)
  }

  async put<T>(url: string, data: any): Promise<T> {
    return this.alovaInst.Put<T>(url, data).send()
  }

  async post<T>(url: string, data: any): Promise<T> {
    return this.alovaInst
      .Post<T>(url, data, {
        headers: {'Content-Type': 'application/json'}
      })
      .send()
  }

  async clearCache(url: string) {
    const methodInstance = this.alovaInst.Get(url)
    await invalidateCache(methodInstance)
  }
}

// 💡 Create a SINGLE instance so caching works globally across your app
const httpService = new MyHttpService()

export const useHttp = () => {
  return httpService
}
